import React, { memo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Snack from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";

// @material-ui/icons
import Close from "@material-ui/icons/Close";

import styles from "assets/jss/material-dashboard-pro-react/components/snackbarContentStyle.js";

const useStyles = makeStyles(styles);

// Offset constants for stacking snackbars
const SNACKBAR_BASE_OFFSET = 40; // px from top for first snackbar
const SNACKBAR_HEIGHT_GAP = 70; // px between each stacked snackbar

/**
 * Individual snackbar notification item.
 * @param {object} snackbar - Snackbar data { id, message, color, position }
 * @param {function} onClose - Callback to dismiss by id
 * @param {React.Component} icon - Optional icon component
 * @param {string} place - Position: "tl"|"tr"|"tc"|"br"|"bl"|"bc"
 * @param {number} offsetIndex - Stack index for vertical offset
 */
const SnackbarItem = memo(({ snackbar, onClose, icon, place, offsetIndex = 0 }) => {
  const classes = useStyles();
  const { message, color, id } = snackbar;
  const position = snackbar.position || place || "tr";
  const IconComp = icon;

  const messageClasses = cx({
    [classes.iconMessage]: icon !== undefined,
    [classes.spanMessage]: classes.spanMessage,
  });

  const iconClasses = cx({
    [classes.icon]: classes.icon,
    [classes.infoIcon]: color === "info",
    [classes.successIcon]: color === "success",
    [classes.warningIcon]: color === "warning",
    [classes.dangerIcon]: color === "danger",
    [classes.primaryIcon]: color === "primary",
    [classes.roseIcon]: color === "rose",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 12000);
    return () => clearTimeout(timer);
  }, [id, onClose]);

  const topOffset = SNACKBAR_BASE_OFFSET + offsetIndex * SNACKBAR_HEIGHT_GAP;

  return (
    <Snack
      classes={{
        anchorOriginTopCenter: classes.top20,
        anchorOriginTopRight: classes.top40,
        anchorOriginTopLeft: classes.top40,
        root: classes.rootSnack,
      }}
      style={{ top: topOffset }}
      anchorOrigin={{
        vertical: "top",
        horizontal:
          position?.indexOf("l") !== -1
            ? "left"
            : position?.indexOf("c") !== -1
              ? "center"
              : "right",
      }}
      open={true}
      message={
        <div>
          {IconComp ? <IconComp className={iconClasses} /> : null}
          <span className={messageClasses}>
            {message || "Error en la petición"}
          </span>
        </div>
      }
      action={[
        <IconButton
          className={`${classes.iconButton}`}
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => onClose(id)}
        >
          <Close className={classes.close} />
        </IconButton>,
      ]}
      ContentProps={{
        classes: {
          root: classes.root + " " + classes[color],
          message: classes.message,
        },
      }}
    />
  );
});

/**
 * Legacy single snackbar component (kept for backward compatibility).
 */
const LegacySnackbar = ({ icon, place, message, color, close, open, closeNotification }) => {
  const classes = useStyles();

  const messageClasses = cx({
    [classes.iconMessage]: icon !== undefined,
    [classes.spanMessage]: classes.spanMessage,
  });
  const iconClasses = cx({
    [classes.icon]: classes.icon,
    [classes.infoIcon]: color === "info",
    [classes.successIcon]: color === "success",
    [classes.warningIcon]: color === "warning",
    [classes.dangerIcon]: color === "danger",
    [classes.primaryIcon]: color === "primary",
    [classes.roseIcon]: color === "rose",
  });

  var action = [];
  if (close !== undefined) {
    action = [
      <IconButton
        className={`${classes.iconButton}`}
        key="close"
        aria-label="Close"
        color="inherit"
        onClick={() => closeNotification()}
      >
        <Close className={classes.close} />
      </IconButton>,
    ];
  }

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      closeNotification();
    }, 12000);
    return () => clearTimeout(timer);
  }, [open, closeNotification]);

  const IconComp = icon;

  return (
    <Snack
      classes={{
        anchorOriginTopCenter: classes.top20,
        anchorOriginTopRight: classes.top40,
        anchorOriginTopLeft: classes.top40,
        root: classes.rootSnack,
      }}
      anchorOrigin={{
        vertical: place?.indexOf("t") === -1 ? "bottom" : "top",
        horizontal:
          place?.indexOf("l") !== -1
            ? "left"
            : place?.indexOf("c") !== -1
              ? "center"
              : "right",
      }}
      open={open}
      message={
        <div>
          {IconComp ? <IconComp className={iconClasses} /> : null}
          <span className={messageClasses}>{message ? message : open ? "Error en la petición" : ''}</span>
        </div>
      }
      action={action}
      ContentProps={{
        classes: {
          root: classes.root + " " + classes[color],
          message: classes.message,
        },
      }}
    />
  );
};

export default function Snackbar(props) {
  const { snackbars = [], onRemove, icon, place, ...legacyProps } = props;

  const handleRemove = useCallback((id) => {
    onRemove(id);
  }, [onRemove]);

  // Queue mode: multiple simultaneous snackbars
  if (snackbars.length > 0 && onRemove) {
    return (
      <>
        {snackbars.map((snackbar, index) => (
          <SnackbarItem
            key={snackbar.id}
            snackbar={snackbar}
            onClose={handleRemove}
            icon={icon}
            place={place}
            offsetIndex={index}
          />
        ))}
      </>
    );
  }

  // Legacy: backward-compatible single snackbar
  return (
    <LegacySnackbar
      icon={icon}
      place={place}
      {...legacyProps}
    />
  );
}

Snackbar.default = {
  color: "info",
};

Snackbar.propTypes = {
  message: PropTypes.node,
  color: PropTypes.oneOf([
    "info",
    "success",
    "warning",
    "danger",
    "primary",
    "rose",
  ]),
  close: PropTypes.bool,
  icon: PropTypes.object,
  /** Position: tl=top-left, tr=top-right, tc=top-center, br=bottom-right, bl=bottom-left, bc=bottom-center */
  place: PropTypes.oneOf(["tl", "tr", "tc", "br", "bl", "bc"]),
  open: PropTypes.bool,
  closeNotification: PropTypes.func,
  snackbars: PropTypes.array,
  onRemove: PropTypes.func,
};
